import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { liftangoBrandTokens } from '../../../../theme';

const BRAND_TARMAC = liftangoBrandTokens.palette.textPrimary;
const TOOLTIP_BORDER = liftangoBrandTokens.simulationResults.tooltipBorder;
const FONT_FAMILY = '"Roboto", sans-serif';

/** z-index above Leaflet panes and map overlays so tooltips stay visible and receive hover. */
const tooltipPopperSx = {
  zIndex: 99999,
  '& .MuiTooltip-tooltip': {
    bgcolor: liftangoBrandTokens.simulationResults.tooltipBackground,
    color: BRAND_TARMAC,
    border: `1px solid ${TOOLTIP_BORDER}`,
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
    p: 1.5,
  },
} as const;

export interface SimulationResultsTooltipProps {
  title: React.ReactNode;
  children: React.ReactNode;
  placement?: 'bottom' | 'top' | 'left' | 'right';
}

/**
 * MUI Tooltip styled like `SimulationResultsSidebar` metric tooltips (light panel, arrow, brand colors).
 * Wraps content in a `cursor: help` span for the same affordance as sidebar metrics.
 */
export const SimulationResultsTooltip: React.FC<SimulationResultsTooltipProps> = ({
  title,
  children,
  placement = 'bottom',
}) => {
  const titleNode =
    typeof title === 'string' ? (
      <Typography
        component="span"
        sx={{
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          maxWidth: 320,
          display: 'block',
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>
    ) : (
      title
    );

  return (
    <Tooltip
      title={titleNode}
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={0}
      slotProps={{
        popper: {
          disablePortal: false,
          sx: tooltipPopperSx,
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'help',
          minWidth: 0,
          pointerEvents: 'auto',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
